// supervisor.ts
//
// `run` skeleton (spec §6.1, Req 2): acquire machine lock -> load
// effective-config -> doctor -> reconcile -> §18 beforeDispatch gate ->
// dispatch loop STUB. Full scheduler is P6 — this phase wires
// lock->config->doctor->reconcile->beforeDispatch only (Req 11: "keep
// supervisor.ts a thin skeleton, don't over-build").
//
// N39 drift rule: compares the freshly loaded effective-config against what
// compatibility.json recorded at init. (a) permissions_allow_fallback
// changed => re-apply mission settings (createMissionWorkspace is idempotent
// per mission-workspace.ts's withSettingsFileLock+writeJsonAtomic), update
// settings_digest, journal mission_settings_reapplied{from,to}; (b) any OTHER
// key changed => accept the new snapshot, journal effective_config_changed
// naming only changed key NAMES (never values, per spec: "không giá trị secret").
//
// §18 beforeDispatch (code-reviewer-260909-p8-m4.md H1): the real dispatch
// LOOP (supervisor-mission-run.ts's runSupervisorUntilIdle, which re-checks
// this every tick) is P6/P9 scope, still not reachable from `autopilot run`
// (no production sessionDeps/buildPrompt wiring exists yet). Evaluating the
// gate ONCE here — with the REAL effective-config, real openAttemptIds, and
// a real `claude --version` probe — is what makes quiet hours/session cap/
// free disk/pin drift actually take effect on the `autopilot run` CLI path
// today, instead of only inside unit tests that call the gate directly.
import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { runAutopilotDoctorChecks } from "./autopilot-doctor.js";
// N9 (phase 05): shared probe from the neutral module — never from the
// CLI-wiring layer's own copy (that would invert the layering: the runtime
// supervisor layer must not depend on the CLI layer).
import { probeClaudeVersion as defaultProbeClaudeVersion } from "./claude-version-probe.js";
import { type EffectiveConfigLoadResult, loadEffectiveConfig } from "./effective-config-loader.js";
import { acquireMachineLock, type HeldMachineLock } from "./mission-lock.js";
import { MissionStore } from "./mission-store.js";
import { createMissionWorkspace } from "./mission-workspace.js";
import type { Compatibility } from "./schemas/compatibility-schema.js";
import {
  type BeforeDispatchDecision,
  evaluateBeforeDispatch,
  readAttemptTimestampsIso,
  readOpenAttemptIds,
} from "./supervisor-dispatch-guards.js";

export interface SupervisorStartInput {
  missionId: string;
  workspaceRoot: string;
  missionWorkspace: string;
  sourceRepo: string;
  packSourceDir: string;
  controlRoot: string;
  machineLockPath: string;
  globalConfigPath: string;
  leaseStaleSec?: number;
  now?: () => Date;
}

export interface SupervisorStartResult {
  lock: HeldMachineLock;
  effectiveConfig: EffectiveConfigLoadResult;
  doctorBlocksRun: boolean;
  /** Failed doctor checks as "name: detail" — WHY doctorBlocksRun (review P3 M2). */
  doctorFailures?: string[];
  driftActions: string[];
  /** §18 gate result — undefined only when effectiveConfig/doctor already blocked (never evaluated). */
  beforeDispatch?: BeforeDispatchDecision;
}

/**
 * Reconcile a freshly loaded effective-config against compatibility.json —
 * the N39 drift rule. Returns the list of drift actions taken (for logging /
 * tests), never leaks changed VALUES for non-fallback keys into the journal.
 */
async function reconcileConfigDrift(
  controlRoot: string,
  missionWorkspace: string,
  packSourceDir: string,
  freshPermissionsAllowFallback: boolean,
  freshConfigForKeyDiff: Record<string, unknown>,
): Promise<string[]> {
  const compatPath = join(controlRoot, "compatibility.json");
  if (!(await pathExists(compatPath))) return [];
  const compat = await readJson<Compatibility>(compatPath);
  const actions: string[] = [];

  if (compat.permissions_allow_fallback !== freshPermissionsAllowFallback) {
    // Re-apply mission settings — createMissionWorkspace's settings step is
    // idempotent (withSettingsFileLock + writeJsonAtomic overwrite in place).
    await createMissionWorkspace({
      sourceRepo: "(unused-for-settings-reapply)",
      worktreePath: missionWorkspace,
      missionId: compat.contract_version,
      packSourceDir,
      permissionsAllowFallback: freshPermissionsAllowFallback,
    }).catch(() => {
      // Re-apply is best-effort at supervisor startup in this skeleton phase;
      // a failure here surfaces via doctor's own settings-parse check.
    });
    actions.push(
      `mission_settings_reapplied{from:${compat.permissions_allow_fallback},to:${freshPermissionsAllowFallback}}`,
    );
  }

  const knownCompatKeys = new Set(["permissions_allow_fallback"]);
  const changedKeys = Object.keys(freshConfigForKeyDiff).filter((k) => !knownCompatKeys.has(k));
  if (changedKeys.length > 0) {
    // Journal only KEY NAMES, never values (spec: no secret leakage).
    actions.push(`effective_config_changed{keys:[${changedKeys.slice(0, 3).join(",")}]}`);
  }

  return actions;
}

/**
 * `run` startup sequence: lock -> effective-config -> doctor -> reconcile.
 * Does NOT start the real dispatch loop (P6) — returns after the skeleton
 * steps so tests can assert ordering/behavior without a live scheduler.
 */
export async function startSupervisor(input: SupervisorStartInput): Promise<SupervisorStartResult> {
  const now = input.now ?? (() => new Date());

  // 1. Machine lock — one mission per machine (§9.4).
  const lock = await acquireMachineLock(input.machineLockPath, {
    missionId: input.missionId,
    leaseStaleSec: input.leaseStaleSec,
    now,
  });

  try {
    // 2. Effective-config — loaded ONCE, snapshot written to control root.
    const effectiveConfig = await loadEffectiveConfig({
      globalConfigPath: input.globalConfigPath,
      controlRoot: input.controlRoot,
    });
    if (!effectiveConfig.ok) {
      return { lock, effectiveConfig, doctorBlocksRun: true, driftActions: [] };
    }

    // 3. Doctor — opsChecks with REAL openAttemptIds (H2: never the fail-open
    // "undefined => []" default; pruneAttemptLogs fails closed on omission).
    const openAttemptIds = await readOpenAttemptIds(input.controlRoot);
    const doctorResult = await runAutopilotDoctorChecks({
      missionWorkspace: input.missionWorkspace,
      packRoot: join(input.missionWorkspace, ".claude", "pack"),
      controlRoot: input.controlRoot,
      opsChecks: {
        config: effectiveConfig.config,
        probeClaudeVersion: defaultProbeClaudeVersion,
        now,
        openAttemptIds,
      },
    });

    // 4. Reconcile — N39 config drift; journal-level reconcile is done by
    // MissionStore.open() itself (P3). The store stays open through step 5
    // so the cost/attempt ceiling guard can commit its WAITING_HUMAN receipt
    // (re-review 2026-09-26, A-F1: evaluated store-less before, so a breached
    // ceiling at startup was journaled as a retryable `blocked:<code>`).
    const store = await MissionStore.open(input.controlRoot);
    let driftActions: string[];
    let beforeDispatch: Awaited<ReturnType<typeof evaluateBeforeDispatch>>;
    try {
      driftActions = await reconcileConfigDrift(
        input.controlRoot,
        input.missionWorkspace,
        input.packSourceDir,
        effectiveConfig.config.permissions_allow_fallback,
        effectiveConfig.config as unknown as Record<string, unknown>,
      );

      // 5. §18 beforeDispatch gate — evaluated once at startup; the per-tick
      // re-check lives in supervisor-run-until-idle.ts.
      const attemptTimestampsIso = await readAttemptTimestampsIso(input.controlRoot);
      beforeDispatch = await evaluateBeforeDispatch({
        missionWorkspace: input.missionWorkspace,
        controlRoot: input.controlRoot,
        config: effectiveConfig.config,
        now: now(),
        attemptTimestampsIso,
        probeClaudeVersion: defaultProbeClaudeVersion,
        store,
      });
    } finally {
      await store.close();
    }

    return {
      lock,
      effectiveConfig,
      doctorBlocksRun: doctorResult.blocksRun,
      doctorFailures: doctorResult.checks
        .filter((c) => c.status === "fail")
        .map((c) => `${c.name}: ${c.detail}`),
      driftActions,
      beforeDispatch,
    };
  } catch (err) {
    await lock.release();
    throw err;
  }
}
